<template>
  <div v-if="!loading">
    <summary-navigator
      :export="exportIndividualList"
      :report-name="reportName"
      @officechanged="officeChanged"
      @companychanged="companyChanged"
    />
    <template v-for="(dateOrders, key) in getOrdersByDate">
      <el-row :key="key">
        <el-col :span="18">
          <table :aria-describedby="$t(`orderList.description`)" class="orders-by-user">
            <thead class="individual-list__table">
              <tr>
                <th scope="col">
                  {{ $t(`orderList.months.${getMonthName(dateOrders[0].menuInfo.date)}`) }}
                  {{ getDayNumber(dateOrders[0].menuInfo.date) }}
                </th>
                <template v-for="mealEvent in mealEvents">
                  <th :key="mealEvent.id" scope="col">
                    {{ $t(`orderList.${mealEvent.name.toLowerCase()}`) }}
                  </th>
                </template>
                <th scope="col">
                  {{ $t(`orderList.email`) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(userOrders, key) in getUsersOrdersByUserName(dateOrders)"
                :key="key"
                :set="(userInfo = userOrders[0].userInfo)"
              >
                <td class="order-name">{{ userInfo.name }}</td>
                <td v-for="mealEvent in mealEvents" :key="mealEvent.id" class="order-diet">
                  {{ getDiet(userOrders, mealEvent) ? getDiet(userOrders, mealEvent) : null }}
                </td>
                <td>{{ userInfo.email }}</td>
              </tr>
            </tbody>
          </table>
        </el-col>
      </el-row>
    </template>
  </div>
  <div v-else>
    <p>{{ $t('orderList.loading') }}</p>
    <el-tabs
      v-loading.fullscreen.lock="loading"
      v-model="activeTab"
      :class="{ loading: loading }"
      tab-position="left"
      type="card"
    >
    </el-tabs>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import get from 'lodash.get';
import moment from 'moment';
import { groupOrdersByPath } from '@/helpers';
import { DEFAULT_COMPANY } from '@/constants';
import SummaryNavigator from '@/components/orders/SummaryNavigator';

const allCompaniesId = 1;
const gravityCompanyId = 2;
const wizelineCompanyId = 3;

const isWizeliner = (email) => email.includes('@wizeline');
const strategies = {
  [gravityCompanyId]: (order) => isWizeliner(order.userInfo.email),
  [wizelineCompanyId]: (order) => !isWizeliner(order.userInfo.email),
};

const filterOrdersByCompany = (company, orders) => {
  const strategy = strategies[company];

  return orders.filter(strategy);
};

export default {
  name: 'IndividualList',
  data() {
    return {
      company: DEFAULT_COMPANY,
      activeTab: 'individual',
    };
  },
  components: {
    SummaryNavigator,
  },
  computed: {
    ...mapGetters('orders', { orders: 'getWeekOrders' }),
    ...mapState('events', { mealEvents: (state) => state.mealEvents }),
    ...mapState('weeklyMenu', { selectedWeek: (state) => state.selectedWeek }),
    ...mapState('offices', { offices: (state) => state.offices }),
    ...mapState('global', {
      adminSelectedOffice: 'adminSelectedOffice',
      adminSelectedCompany: 'adminSelectedCompany',
    }),
    ...mapState('orders', { loading: 'loading' }),
    companyOrders() {
      if (this.company === allCompaniesId) {
        return this.orders;
      }

      return filterOrdersByCompany(this.company, this.orders);
    },
    officeName() {
      if (Object.keys(this.offices).length === 0) {
        return '';
      }
      const office = this.offices.find((o) => o.id === this.adminSelectedOffice);

      return office.name;
    },
    reportName() {
      const report = 'individual';
      return `${this.officeName}-${report}-${this.selectedWeek}.csv`;
    },
    getOrdersByDate() {
      return groupOrdersByPath(this.companyOrders, 'menuInfo.date');
    },
    exportIndividualList() {
      const data = [];
      Object.keys(this.getOrdersByDate).forEach((date) => {
        const orders = this.getOrdersByDate[date];

        // Headers
        const header = [];
        header.push(date);
        this.mealEvents.forEach((mealEvent) => {
          header.push(this.$t(`orderList.${mealEvent.name}`));
        });
        header.push(this.$t(`orderList.email`));
        data.push(header);

        // Body
        const ordersByDate = this.getUsersOrdersByUserName(orders);
        Object.keys(ordersByDate).forEach((ordersKey) => {
          const userRow = [];
          const userOrders = ordersByDate[ordersKey];
          const { name, email } = userOrders[0].userInfo;

          userRow.push(name);

          this.mealEvents.forEach((mealEvent) => {
            const dietForMealEvent = this.getDiet(userOrders, mealEvent);
            if (dietForMealEvent) {
              userRow.push(dietForMealEvent);
            } else {
              userRow.push(null);
            }
          });
          userRow.push(email);
          data.push(userRow);
        });
      });
      return data;
    },
  },
  methods: {
    getDayNumber(date) {
      return moment(date).format('D');
    },
    getMonthName(date) {
      return moment(date)
        .format('MMMM')
        .toLowerCase();
    },
    getDiet(orders, mealEvent) {
      const result = orders
        .filter((order) => order.menuInfo.event === mealEvent.id)
        .map((event) => get(event, 'mealInfo.dietInfo.name', null));

      const dietSelected = result.join(' + ');

      return dietSelected || null;
    },
    getUsersOrdersByUserName(orders) {
      return groupOrdersByPath(orders, 'userInfo.name');
    },
    officeChanged(office) {
      this.$emit('officechanged', office);
    },
    companyChanged(company) {
      this.company = company;
      this.$emit('companychanged', company);
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../scss/variables';
.orders-by-user {
  width: 100%;
  margin-top: 2em;
  .order-name {
    width: 55%;
  }
  .order-diet {
    width: 15%;
  }
  td {
    border: 1px solid $orders-by-user-border;
    padding: 0.1em 0.5em;
  }
}
#location {
  font-weight: 500;
}
.column-header {
  margin: 2em auto 1em;
}

.individual-list__table {
  th {
    padding-left: 0.5em;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/orders/IndividualList.vue